/**
 * Shared "Issues" column used by every exception table.
 *
 * The chip surfaces how many open exception types the row's object
 * currently carries within its data_domain AND doubles as the primary
 * trigger for the per-row ``ExceptionDrawer``. A small teal dot in
 * the top-right of the chip signals the row carries notes.
 *
 * Position is per-config: the chip explains WHY the row exists, so
 * configs scan it next to the item name (products place it after the
 * product name; sales-compare places it at column 0, which puts it
 * right after the auto-injected bulk-tick). Configs can opt the
 * column into a freeze edge if their layout needs it; the builder
 * itself doesn't freeze.
 *
 * row.errors is tag-backed for every domain - the tag-table aggregation
 * in backend/shared/exception_queries/row_errors.py (inventory rows
 * attach at the resolver seam, family-wide), so the chip count always
 * matches the drawer's Issues tab. Shape: `ProductRowError` - see
 * renderErrorCount.
 *
 * Closed-view rows additionally carry row.recently_closed_errors[];
 * the renderer flips to a teal CheckCircle chip in that case.
 */
import { renderErrorCount, type ProductRowError } from './productRenderers'
import { actionableEntries } from './issuesState'
import type { ExceptionColumnDef } from '../types'

/**
 * Fixed pixel width for the Issues column. Applied to both header and
 * body cells via ``width / minWidth / maxWidth`` so MUI's auto-layout
 * table can't stretch the column to match a neighbour - cumulative
 * freeze offsets and the centred sort-icon overlay only stay stable
 * when all three width fields agree.
 *
 * Sized to fit the chip (``minWidth: 38`` in ``issuesState.chipSxFor``)
 * plus its two corner indicator badges, which are absolutely positioned and
 * overhang -9px without being counted in layout: the fix-marker pennant on the
 * top-left and the note badge on the top-right. Body cells are CENTRE-aligned
 * (``align: 'center'``), so the slack over the bare chip splits evenly either
 * side - giving both corner badges symmetric breathing room and keeping the
 * left pennant clear of the frozen checkbox column (the old right-aligned
 * layout pushed the chip flush right and crowded that pennant). The "Issues"
 * header is centred too (``headerAlign: 'center'``) so the label + its sort
 * arrow sit centred over the centred chips rather than reading as off-axis
 * against a left-aligned heading.
 */
export const ISSUES_COLUMN_WIDTH = 96

/**
 * Right padding on the Issues body cell. The default ``size="small"`` cell
 * pads 16px each side; the extra ~16px here shifts the centre-aligned chip
 * (and the data-explorer age caption) ~8px left so it lines up under the
 * centred "Issues" header label, which itself sits slightly left-of-centre
 * because the sort arrow reserves layout space even while hidden. One value
 * to tune the nudge.
 */
export const ISSUES_COLUMN_NUDGE_PADDING_RIGHT = '32px'

/**
 * Build the shared Issues column. Call this once per ExceptionConfig
 * and splice the result wherever in ``columns`` the config wants the
 * chip to land. The builder returns an unfrozen column; configs that
 * need a freeze edge wrap the result and add ``freeze: 'left'``.
 *
 * ``spansSystems: true`` is a no-op on non-split tables, so simple
 * configs aren't affected; on ``rowSplit`` tables (e.g. the LS<->Xero
 * compare-sales view) it merges the two system rows under one cell so
 * the per-sale issue count renders once instead of twice.
 */
export const buildIssuesColumn = (): ExceptionColumnDef => ({
  key: 'errors',
  label: 'Issues',
  align: 'center',
  headerAlign: 'center',
  width: ISSUES_COLUMN_WIDTH,
  minWidth: ISSUES_COLUMN_WIDTH,
  maxWidth: ISSUES_COLUMN_WIDTH,
  spansSystems: true,
  // The centred header label reads a touch left-of-centre because the sort
  // arrow reserves space to its right even when hidden. Nudge the centred
  // body content (chip + age caption) left to sit under the label: extra
  // right padding shifts centre-aligned content left by half its value.
  // ~8px nudge here; single value to tune.
  getCellStyle: () => ({ paddingRight: ISSUES_COLUMN_NUDGE_PADDING_RIGHT }),
  // Visible on both inspector and platform reports. Platform reports
  // span FPs; the chip still surfaces per-row issue state + notes via
  // the same ExceptionDrawer the inspector mounts, so reviewers don't
  // have to deep-link per FP to triage. The chip is a no-op on rows
  // without a tag tuple (row.errors === []) so non-exception platform
  // reports that happen to spread this column would render '-'.
  contexts: ['inspector', 'platform'],
  render: renderErrorCount,
  // Sort by the displayed count: actionable occurrences after collapsing the
  // weight-0 umbrella, matching the chip + the backend ``open_issues`` - so a
  // product negative at 4 outlets sorts as the "4" the chip shows.
  getSortValue: row =>
    Array.isArray(row.errors)
      ? actionableEntries(row.errors as ProductRowError[]).length
      : 0,
  exportFormat: 'text',
})
